import { ComponentType, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import useAxios from "axios-hooks";

import {
  getLocalStorage,
  validateMenuJSONResponse,
} from "features/components/helperFunctions";
import { callBranchesAPI } from "features/components/businessBranches";
import { projectRoutes } from "utils/constants/Routes";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import { dispatch, useSelector } from "store";
import { openSnackbar } from "store/slices/snackbar";
import {
  setPermittedRoutes,
  setSideBarNavigation,
} from "store/slices/AccessManagement";
import ErrorBoundary from "components/ErrorBoundries";
import ErrorPage from "components/ErrorBoundries/error-page";

const AccessManagement = () => (WrappedComponent: ComponentType<any>) => {
  const AuthorizeComponent = () => {
    const { permittedRoutes } = useSelector((state) => state.accessManagement);
    const { businessBranchesAccess } = useSelector(
      (state) => state.businessBranches
    );

    const { pathname } = useLocation();

    const { eatout_id, user_id } = getLocalStorage();

    //======================================= API Call Payloads =======================================//

    // menu items json API call payload
    const menuItemsAPIPayload = () => {
      const formData = new FormData();

      formData.append("business_id", `${eatout_id}`);
      formData.append("user_id", `${user_id}`);
      formData.append("source", "biz");
      return formData;
    };

    // menu items json API call
    const [{ error: menuJSONError }, menuItemsAPICall] = useAxios(
      { ...API_END_POINTS.getMenuJSON, data: menuItemsAPIPayload() },
      { manual: true }
    );

    //======================================= useEffect Hooks =======================================//

    useEffect(() => {
      // api call to get business branches
      if (!businessBranchesAccess.length) {
        callBranchesAPI();
      }
    }, []);

    useEffect(() => {
      // calling menu json api here to populate the redux store on refresh
      if (permittedRoutes.length === 1 && businessBranchesAccess.length) {
        callMenuJSONAPI();
      }
    }, [businessBranchesAccess]);

    // useEffect to handle API error states
    useEffect(() => {
      if (menuJSONError) {
        // notify user
        dispatch(
          openSnackbar({
            open: true,
            message: menuJSONError ? menuJSONError.message : "",
            alert: {
              color: "error",
            },
          })
        );
      }
    }, [menuJSONError]);

    //======================================= Handler Functions =======================================//

    const callMenuJSONAPI = async () => {
      // menu items json API call
      const {
        data: { data, status },
      } = await menuItemsAPICall({
        data: menuItemsAPIPayload(),
      });

      // status 200 (getting required data in API response)
      if (status === "200" && !Array.isArray(data)) {
        if (data?.items?.length) {
          // storing menu json for menuList component to populate sidebar
          dispatch(setSideBarNavigation(data?.items));

          if (validateMenuJSONResponse(data)) {
            let permittedRoutesList = projectRoutes.reduce(
              (routes: string[], route) => {
                if (data?.access?.modules?.includes(route.id)) {
                  routes = [...routes, ...route.routes];
                }

                return routes;
              },
              []
            );

            // storing permitted modules routes to protect others
            dispatch(setPermittedRoutes(permittedRoutesList));
          } else {
            // storing all modules routes
            dispatch(
              setPermittedRoutes(
                projectRoutes.flatMap((module) => module.routes)
              )
            );
          }
        }
      }
    };

    if (permittedRoutes.length === 1) {
      return <></>;
    }

    return (
      <ErrorBoundary fallback={<ErrorPage />}>
        {permittedRoutes.includes(pathname) ? (
          <WrappedComponent />
        ) : (
          <Navigate to="/unauthorized-access" />
        )}
      </ErrorBoundary>
    );
  };

  return AuthorizeComponent;
};

export default AccessManagement;
